import { useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import styled, { css } from 'styled-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getViewportWidthQuery, mediaMLgMax, mediaMSmMax, mediaSmMax, mediaXlMin, sizes } from 'helpers/breakpoints';
import CloseIcon from 'assets/scss/fortawesome/icons/svg/close.svg';
import { useBreakpoint } from 'components/BreakpointProvider';
import { selectedBookingPackageAtom } from 'components/forms/bookingWidget/jotaiStore';
import { getBoatPhotoUrls } from 'helpers';
import NextImageWithShimmer, { getPathnameFromUrl } from 'components/image/NextImageWithShimmer';
import { FlexAlignCenterSpaceBetween, FlexCol } from '../../primitives/flex';
import { Text } from '../../primitives/typography';
import { mobileBookingPanelVisibleAtom } from '../store';
import useConversationDetail from '../hooks/useConversationDetail';
import { Box } from '../../primitives/box';
import SidebarBookingActions from './SidebarBookingActions';
import BookingInfo from './BookingInfo';
import BookingPricing from './BookingPricing';
import MessageCaptainOwnerInfo from './MessageCaptainOwnerInfo';
import { panelMobileStyles, panelScrollMixin } from '../commons';
import ConversationHeader from '../ConversationHeader';

// TODO move to separate config file
dayjs.extend(utc);
dayjs.extend(timezone);

export default function BookingPanel() {
  const [isMobileBookingPanelVisible, setMobileBookingPanelVisible] = useAtom(mobileBookingPanelVisibleAtom);
  const {
    data,
    data: {
      booking: { state, boat },
      booking,
    },
    conversationId: activeConversationId,
    isFetching: isLoadingConversation,
  } = useConversationDetail();
  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;
  const setSelectedBookingPackage = useSetAtom(selectedBookingPackageAtom);

  useEffect(() => {
    setSelectedBookingPackage(booking.package);
  }, [setSelectedBookingPackage, booking.package]);

  const boatPhotoUrl = getBoatPhotoUrls([boat.best_photo])[0];
  const boatPhotoSizes =
    '(min-width: 1141px) calc((100vw * 0.2) - 60px), (min-width: 640px) calc((100vw * 0.35) - 60px), calc(100vw - 60px)';

  return (
    <Wrapper isVisibleOnMobile={isMobileBookingPanelVisible}>
      {smallScreen && <ConversationHeader conversation={data} hideBackButton />}
      <Panel
        padding="30px"
        isVisibleOnMobile={isMobileBookingPanelVisible}
        data-testid="components-inbox-booking-panel"
      >
        <FlexAlignCenterSpaceBetween>
          <Text fWeight={600} fSize="22px">
            {state === 'initialized' || state === 'pre_approved' ? 'Inquiry details' : 'Booking information'}
          </Text>
          <StyledCloseIcon width={20} onClick={() => setMobileBookingPanelVisible(false)} />
        </FlexAlignCenterSpaceBetween>
        <Box
          as="a"
          position="relative"
          href={`/boats/${boat.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ aspectRatio: '4/3', marginTop: '15px', marginBottom: '15px' }}
        >
          <NextImageWithShimmer
            alt={boat.listing_tagline}
            src={getPathnameFromUrl(boatPhotoUrl)}
            sizes={boatPhotoSizes}
            priority
            fill
            style={{
              objectFit: 'cover',
            }}
          />
        </Box>
        <Text fWeight={600} pb="15px">
          {boat.listing_tagline}
        </Text>
        <SidebarBookingActions
          userType={booking.user_type}
          booking={booking}
          conversationId={activeConversationId}
          // todo add check here
          hideBookingActions={false}
        />
        <Divider />

        <BookingInfo />

        <Divider />

        {boat.captain_network_enabled && booking.package_type === 'captained' && (
          <>
            <MessageCaptainOwnerInfo
              userType={booking.user_type}
              captain={booking.captain}
              owner={booking.owner}
              bookingId={booking.id}
              conversationId={activeConversationId}
              captainState={booking.captain_state}
              boatId={boat.id}
              isLoadingConversation={isLoadingConversation}
            />
            <Divider />
          </>
        )}

        <BookingPricing />
      </Panel>
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)<{ isVisibleOnMobile: boolean }>`
  ${mediaMSmMax} {
    transform: translateY(-101%);

    ${({ isVisibleOnMobile }) =>
      isVisibleOnMobile &&
      css`
        transform: translateY(0%);
      `};
  }

  ${mediaMLgMax} {
    ${panelMobileStyles};
    transition: transform 0.3s;
  }

  ${getViewportWidthQuery(sizes.width.MD.min, sizes.width.LG.max)} {
    width: calc(65% - 1px);
    left: unset;
    transform: translateX(100%);

    ${({ isVisibleOnMobile }) =>
      isVisibleOnMobile &&
      css`
        transform: translateX(0%);
      `};
  }
`;

const Panel = styled(FlexCol)<{ isVisibleOnMobile: boolean }>`
  ${panelScrollMixin}
`;

const Divider = styled.hr`
  margin: 30px 0;
  border: solid #dbdfe5;
  border-width: 1px 0 0;
`;

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  @media ${mediaSmMax} {
    display: none;
  }
  @media ${mediaXlMin} {
    display: none;
  }
`;
